import type { ReactNode } from 'react'
import { core } from '@musinsa/pbo-core'
import { Icon as DsIcon } from '@musinsa/pbo-assets'

/**
 * pbo-core(2.0 신규 디자인) 화면의 알럿·확인창 — 옛 mwms-ds 모달(`MAlert`·`MConfirm`, 검정 버튼)을 대신한다.
 *
 * 모양은 컨덕터 codegen 이 확인창(core.ConfirmModal)으로 내보내는 것과 같다 — `core.Modal.confirm` + DS 경고 아이콘
 * (`ic-alertCircle` 22 #faad14, antd 기본 글리프를 쓰면 이 모달만 antd 아이콘이 남는다) + 제목 "알림" + 문구 pre-line.
 * 알럿은 같은 모양에 버튼 하나(`core.Modal.warning`)다. 중앙 정렬은 core.Modal 명령형 API 가 이미 건다.
 * 서체는 화면 screenShell.css 의 포털 규칙(`body.pbo-screen-portal .ant-modal-root`)이 맡는다.
 */
const DIALOG_TITLE = '알림'

export const PBO_WARNING_ICON = (
  <span style={{ display: 'inline-flex', lineHeight: 0, marginInlineEnd: 12 }}>
    <DsIcon name="ic-alertCircle" size={22} color="#faad14" filled />
  </span>
)

/** Modal.confirm 은 문자열의 \n 을 접는다 — 줄바꿈이 든 문구도 살린다(codegen 과 같은 pre-line). */
const toContent = (content: ReactNode): ReactNode =>
  typeof content === 'string' ? <span style={{ whiteSpace: 'pre-line' }}>{content}</span> : content

/** 지금 떠 있는 알럿 문구 — 같은 문구를 겹쳐 쌓지 않는다(옛 MAlert dedupe 와 같은 규칙, 문자열만). */
const openAlerts = new Set<string>()

export const PboAlert = {
  /** onOk — [확인] 을 눌렀을 때만 부른다(ESC·X 로 닫으면 안 부른다). */
  show: (content: ReactNode, { onOk }: { onOk?: () => void } = {}): void => {
    const key = typeof content === 'string' ? content : null
    if (key !== null) {
      if (openAlerts.has(key)) return
      openAlerts.add(key)
    }
    core.Modal.warning({
      icon: PBO_WARNING_ICON,
      title: DIALOG_TITLE,
      content: toContent(content),
      okText: '확인',
      onOk,
      afterClose: () => {
        if (key !== null) openAlerts.delete(key)
      },
    })
  },
}

interface ConfirmOptions {
  /** [확인] — 모달은 바로 닫고 요청은 뒤에서 돈다(결과 안내는 호출부가 토스트·알럿으로). */
  onConfirm: () => unknown
  /** [취소]·닫기(ESC·X) — 진행하지 않았다. */
  onClose?: () => void
  okText?: string
  cancelText?: string
}

export const PboConfirm = {
  show: (content: ReactNode, { onConfirm, onClose, okText = '확인', cancelText = '취소' }: ConfirmOptions): void => {
    core.Modal.confirm({
      icon: PBO_WARNING_ICON,
      title: DIALOG_TITLE,
      content: toContent(content),
      okText,
      cancelText,
      // promise 를 돌려주지 않는다 — 돌려주면 모달이 응답까지 열려 있다. 실패 안내는 onConfirm 이 맡는다(여기서 삼키는 건 미처리 거부 방지뿐).
      onOk: () => {
        Promise.resolve(onConfirm()).catch(() => undefined)
      },
      onCancel: () => onClose?.(),
    })
  },
}
